import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, SlidersHorizontal } from 'lucide-react';
import clsx from 'clsx';
import { ApiError } from '@/lib/api';
import { listAuditLogColumnsApi, listAuditLogsApi } from '@/lib/audit-api';
import {
  AUDIT_COLUMNS,
  AUDIT_PAGE_SIZE_OPTIONS,
  DEFAULT_AUDIT_FILTER,
  buildAuditQuery,
  normalizeAuditPageSize,
  resolveAuditColumns,
  toggleAuditColumn,
  type AuditFilterState,
} from '@/lib/audit-filters';
import type { AuditLogListResult } from '@/lib/types';
import { Card } from '@/components/ui/card';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/empty-state';
import { formatDateTime } from '@/lib/format';

const SECONDARY_BTN =
  'inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs text-slate-600 transition hover:bg-slate-50 disabled:opacity-60';
const INPUT =
  'rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';

/**
 * Issue #224 — 登录日志 / 操作日志工作区（ADMIN+）。复用员工管理的高级搜索 / 列隐藏 /
 * 默认 20/页分页体验。数据来自真实接口 GET /admin/audit-logs（服务端分页 + org 隔离），
 * fetch 错误经 ApiError 给出 HTTP 状态码 + 可读信息，绝不前端假数据。
 */
export function AuditLogWorkspace() {
  const [filter, setFilter] = useState<AuditFilterState>(DEFAULT_AUDIT_FILTER);
  const [pendingKeyword, setPendingKeyword] = useState('');
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    resolveAuditColumns(null),
  );
  const [columnPanelOpen, setColumnPanelOpen] = useState(false);

  const columnsQuery = useQuery({
    queryKey: ['admin', 'audit-logs', 'columns'],
    queryFn: () => listAuditLogColumnsApi(),
    staleTime: 5 * 60 * 1000,
  });

  const query = useQuery({
    queryKey: ['admin', 'audit-logs', filter],
    queryFn: () => listAuditLogsApi(buildAuditQuery(filter)),
    placeholderData: (prev) => prev,
  });

  const events = columnsQuery.data?.events ?? [];
  const data: AuditLogListResult | undefined = query.data;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;
  const colVisible = (key: string) => visibleColumns.includes(key);

  const patch = (next: Partial<AuditFilterState>) =>
    setFilter((prev) => ({ ...prev, ...next, page: next.page ?? 1 }));

  const errorText = useMemo(() => {
    if (!query.isError) return null;
    const e = query.error;
    return e instanceof ApiError
      ? `加载失败（HTTP ${e.status}）：${e.message}`
      : e instanceof Error
        ? e.message
        : '加载失败，请重试';
  }, [query.isError, query.error]);

  return (
    /* Issue #226 / #229：移除页面冗余说明文案，仅保留标题；功能（高级搜索 / 列隐藏 / 分页 / 归档）不变。 */
    <Card title="登录日志 / 操作日志">
      {/* 高级搜索 */}
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs text-slate-500">
          <span>事件类型</span>
          <select
            className={INPUT}
            value={filter.action}
            onChange={(e) => patch({ action: e.target.value })}
            data-testid="audit-action"
          >
            <option value="">全部事件</option>
            {events.map((ev) => (
              <option key={ev.value} value={ev.value}>
                {ev.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-500">
          <span>关键词（操作者 / 资源ID）</span>
          <div className="flex items-center gap-1">
            <input
              className={clsx(INPUT, 'w-48')}
              value={pendingKeyword}
              placeholder="搜索…"
              onChange={(e) => setPendingKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') patch({ keyword: pendingKeyword });
              }}
              data-testid="audit-keyword"
            />
            <button type="button" className={SECONDARY_BTN} onClick={() => patch({ keyword: pendingKeyword })}>
              <Search className="h-3.5 w-3.5" />
              查询
            </button>
          </div>
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-500">
          <span>起始</span>
          <input
            type="date"
            className={INPUT}
            value={filter.startDate}
            onChange={(e) => patch({ startDate: e.target.value })}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-500">
          <span>结束</span>
          <input
            type="date"
            className={INPUT}
            value={filter.endDate}
            onChange={(e) => patch({ endDate: e.target.value })}
          />
        </label>
        {/* Issue #229：默认软隐藏 2026-06-12 23:40 前历史日志；勾选可审计查看归档（数据从不删除）。 */}
        <label className="flex items-center gap-1.5 self-end pb-1.5 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={filter.includeArchived}
            onChange={(e) => patch({ includeArchived: e.target.checked })}
            data-testid="audit-include-archived"
          />
          <span>包含历史归档日志</span>
        </label>
        <button
          type="button"
          className={clsx(SECONDARY_BTN, 'ml-auto')}
          onClick={() => setColumnPanelOpen((v) => !v)}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          列显示
        </button>
      </div>

      {columnPanelOpen ? (
        <div className="mb-3 flex flex-wrap gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
          {AUDIT_COLUMNS.map((col) => (
            <label
              key={col.key}
              className={clsx('flex items-center gap-1.5 text-xs', col.hideable ? 'text-slate-600' : 'text-slate-400')}
            >
              <input
                type="checkbox"
                checked={colVisible(col.key)}
                disabled={!col.hideable}
                onChange={() => setVisibleColumns((prev) => toggleAuditColumn(prev, col.key))}
              />
              {col.label}
            </label>
          ))}
        </div>
      ) : null}

      {query.isLoading ? (
        <LoadingState />
      ) : errorText ? (
        <ErrorState title="审计日志加载失败" description={errorText} retry={() => query.refetch()} />
      ) : !data || data.items.length === 0 ? (
        <EmptyState
          title="暂无审计日志"
          description="发生登录 / 退出 / SOP 提交 / 质检录音 / AI 评分等操作后，会在这里按时间倒序展示。"
        />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                  {colVisible('createdAt') ? <th className="py-2 pr-3">时间</th> : null}
                  {colVisible('actionLabel') ? <th className="py-2 pr-3">事件</th> : null}
                  {colVisible('operatorName') ? <th className="py-2 pr-3">操作者</th> : null}
                  {colVisible('summary') ? <th className="py-2 pr-3">摘要</th> : null}
                  {colVisible('resourceType') ? <th className="py-2 pr-3">资源类型</th> : null}
                  {colVisible('resourceId') ? <th className="py-2 pr-3">资源ID</th> : null}
                  {colVisible('ipAddress') ? <th className="py-2 pr-3">IP</th> : null}
                </tr>
              </thead>
              <tbody>
                {data.items.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100" data-testid={`audit-row-${row.id}`}>
                    {colVisible('createdAt') ? (
                      <td className="py-2 pr-3 text-slate-500">{formatDateTime(row.createdAt)}</td>
                    ) : null}
                    {colVisible('actionLabel') ? (
                      <td className="py-2 pr-3">
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700">
                          {row.actionLabel}
                        </span>
                      </td>
                    ) : null}
                    {colVisible('operatorName') ? (
                      <td className="py-2 pr-3 text-slate-700">{row.operatorName ?? '—'}</td>
                    ) : null}
                    {colVisible('summary') ? (
                      <td className="py-2 pr-3 text-slate-600">{row.summary ?? '—'}</td>
                    ) : null}
                    {colVisible('resourceType') ? (
                      <td className="py-2 pr-3 text-slate-500">{row.resourceType}</td>
                    ) : null}
                    {colVisible('resourceId') ? (
                      <td className="py-2 pr-3 text-slate-400">{row.resourceId ?? '—'}</td>
                    ) : null}
                    {colVisible('ipAddress') ? (
                      <td className="py-2 pr-3 text-slate-400">{row.ipAddress ?? '—'}</td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <span>
              共 {data.total} 条 · 第 {data.page}/{totalPages} 页
            </span>
            <div className="flex items-center gap-2">
              <select
                className={INPUT}
                value={data.limit}
                onChange={(e) => patch({ pageSize: normalizeAuditPageSize(Number(e.target.value)) })}
                data-testid="audit-page-size"
              >
                {AUDIT_PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n} 条/页
                  </option>
                ))}
              </select>
              <button
                type="button"
                className={SECONDARY_BTN}
                disabled={data.page <= 1}
                onClick={() => patch({ page: data.page - 1 })}
              >
                上一页
              </button>
              <button
                type="button"
                className={SECONDARY_BTN}
                disabled={data.page >= totalPages}
                onClick={() => patch({ page: data.page + 1 })}
              >
                下一页
              </button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
